import React, {useEffect, useState, useMemo} from 'react';
import useTextField from '../utility/hooks/useTextField';
import {validateFieldLength, validateGroupFilters} from '../utility/validationUtil';
import {useGetStudentFiltersQuery} from '../api/services/studentService';
import AdmicityDialog from './AdmicityDialog';
import {Box} from '@mui/material';
import PropTypes from 'prop-types';
import findParentNodes from '../utility/groupFilterTreeUtil';
import {GROUP_MEMBERS_TYPE} from '../constants/GroupMembersType';
import {useGetAllEmployeeProfilesQuery} from '../api/services/employeeService';
import {
    useGetApplicationStatusesQuery,
    useGetCurrentSchoolQuery
} from '../api/services/schoolManagementService';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import FiltersTab from '../pages/SchoolDashboard/Profiles/DownloadReportPopup/FiltersTab';
import FormGroup from '@mui/material/FormGroup';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import Alert from '@mui/material/Alert';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import SettingsSectionBase from '../pages/SchoolDashboard/Forms/PrivateFormSettings/SettingsSection';
import LinkIcon from '@mui/icons-material/Link';
import Typography from '@mui/material/Typography';
import TextSnippetOutlinedIcon from '@mui/icons-material/TextSnippetOutlined';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import getCheckboxDisplayLabel from '../utility/getCheckboxDisplayLabel';

const initialSettings = {
    groupFilters: [],
    studentFilters: [],
    applicationFilters: [],
    enquiryFilters: [],
    groupCategory: null,
    employeeFilters: [],
    membersType: GROUP_MEMBERS_TYPE.students,
    statusUpdateEnabled: false,
    membershipSetStatusId: '',
    membershipFromStatusId: ''
};

const groupFilterValidationInitialState = {isValid: true};

const CreateOrUpdateGroupDialog = ({
                                       open,
                                       title,
                                       group,
                                       formCategories,
                                       onClose,
                                       onSave,
                                       groupFilters,
                                       editMode = false,
                                       isLoading
                                   }) => {
    const {data: schoolInfo} = useGetCurrentSchoolQuery();
    const [filtersValidation, setFiltersValidation] = useState(groupFilterValidationInitialState);
    const nameField = useTextField({
        initialValue: group.name ?? '',
        validate: (value) => validateFieldLength(value, 256),
    });
    const {
        data: studentsData,
    } = useGetStudentFiltersQuery({
        body: {
            filterId: group.parentGroupFilterId,
            studentIds: group.students
        }
    }, {skip: !open || !group.students || group.students.length === 0});

    const {
        data: employeeProfilesData,
    } = useGetAllEmployeeProfilesQuery({
        body: {
            filterId: group.parentGroupFilterId,
            employeeIds: group.employeeIds
        }
    }, {skip: !open || !group.employeeIds || group.employeeIds.length === 0});

    const [settings, setSettings] = useState({...initialSettings});
    const [strictRecipientsFiltering, setStrictRecipientsFiltering] = useState(false);
    const [isGroupDynamic, setIsGroupDynamic] = useState(true);
    const {data: applicationStatuses = []} = useGetApplicationStatusesQuery();

    const filteredGroupFilters = useMemo(() => {
        if (!groupFilters) return groupFilters;

        if (settings.membersType === GROUP_MEMBERS_TYPE.employee) {
            return groupFilters.filter(group => group.name === 'EMPLOYEES');
        } else {
            return groupFilters.filter(group => group.name !== 'EMPLOYEES');
        }
    }, [groupFilters, settings.membersType]);

    const memberTypeTitlePlural = settings.membersType === GROUP_MEMBERS_TYPE.employee ? 'Employees' : 'Students';

    useEffect(() => {
        if (!open) {
            nameField.reset();
            setSettings({...initialSettings});
            setFiltersValidation(groupFilterValidationInitialState);
            setStrictRecipientsFiltering(false);
            setIsGroupDynamic(true);
        } else {
            if (!isLoading) {
                nameField.setValue(group.name ?? '');
                setSettings((prev) => ({
                    ...prev,
                    groupFilters: group.groupFilters?.filter((filter) => getDisplayName(filter.filterId))
                        .map((filter) => ({
                            id: filter.filterId,
                            displayName: getDisplayName(filter.filterId),
                            excluded: filter.excluded,
                        })) ?? [],
                    groupCategory: group.category ?? null,
                    membersType: group.membersType ?? GROUP_MEMBERS_TYPE.students,
                    statusUpdateEnabled: !!(
                        group.membershipSetStatusId ??
                        group.toApplicationStatusId ??
                        group.statusUpdateToStatusId ??
                        group.toStatusId
                    ),
                    membershipSetStatusId: (
                        group.membershipSetStatusId ??
                        group.toApplicationStatusId ??
                        group.statusUpdateToStatusId ??
                        group.toStatusId ??
                        ''
                    ),
                    membershipFromStatusId: (
                        group.membershipFromStatusId ??
                        group.fromApplicationStatusId ??
                        group.statusUpdateFromStatusId ??
                        group.fromStatusId ??
                        ''
                    )
                }));
                if (group && Object.keys(group).length > 0) {
                    setStrictRecipientsFiltering(group.strictRecipientsFiltering);
                    setIsGroupDynamic(group.isDynamic);
                }
            }
        }
    }, [open, isLoading]);

    const getDisplayName = (filterId) =>
        findParentNodes(groupFilters, filterId).map((x) => x.name).join(' - ');

    useEffect(() => {
        if (studentsData?.length) {
            const filters = studentsData.reduce((acc, item) => {
                if (item.isApplicant) {
                    acc.applicationFilters.push(item);
                } else if (item.isStudent) {
                    acc.studentFilters.push(item);
                } else if (item.isEnquiry) {
                    acc.enquiryFilters.push(item);
                }
                return acc;
            }, {
                applicationFilters: [],
                studentFilters: [],
                enquiryFilters: []
            });
            setSettings((prev) => ({
                ...prev,
                ...filters
            }));
        }
    }, [studentsData?.length]);

    useEffect(() => {
        if (employeeProfilesData?.length) {
            setSettings((prev) => ({
                ...prev,
                employeeFilters: employeeProfilesData.map(item => ({
                    id: item.id,
                    preferredForename: item.preferredForename,
                    preferredSurname: item.preferredSurname
                })),
            }));
        }
    }, [employeeProfilesData?.length]);

    useEffect(() => {
        setFiltersValidation(
            settings.groupFilters.length > 0
                ? validateGroupFilters(settings.groupFilters)
                : groupFilterValidationInitialState
        );
    }, [settings.groupFilters]);

    const handleCategoryChange = (e) => {
        const selectedCategoryId = e.target.value || null;
        setSettings((prev) => ({
            ...prev,
            groupCategory: selectedCategoryId,
        }));
    };

    const handleMembersTypeChange = (e) => {
        const newMembersType = Number.parseInt(e.target.value);
        setSettings((prev) => ({
            ...prev,
            membersType: newMembersType,
            groupFilters: [],
            studentFilters: [],
            applicationFilters: [],
            enquiryFilters: [],
            employeeFilters: [],
            statusUpdateEnabled: false,
            membershipSetStatusId: '',
            membershipFromStatusId: ''
        }));
    };

    const isStudentDynamicGroup = settings.membersType === GROUP_MEMBERS_TYPE.students && isGroupDynamic;
    const isSaveDisabled = (
        nameField.error ||
        !nameField.value ||
        !filtersValidation.isValid ||
        isLoading ||
        (isStudentDynamicGroup && settings.statusUpdateEnabled && !settings.membershipSetStatusId)
    );

    return (
        <AdmicityDialog
            handleClose={onClose}
            title={title}
            open={open}
            maxWidth="md"
            actions={[
                {
                    label: 'Cancel',
                    onClick: onClose,
                },
                {
                    label: 'Save',
                    disabled: isSaveDisabled,
                    tooltip:
                        (!filtersValidation.isValid && filtersValidation.error)
                            ? filtersValidation.error
                            : (isStudentDynamicGroup && settings.statusUpdateEnabled && !settings.membershipSetStatusId)
                                ? 'Select a To Status or disable Status Update Configuration.'
                                : null,
                    onClick: () =>
                        onSave({
                            ...settings,
                            groupName: nameField.value,
                            strictRecipientsFiltering,
                            formCategoryId: settings.groupCategory,
                            isGroupDynamic,
                        }),
                },
            ]}
        >
            {
                isLoading
                    ? <LoadingSkeleton/>
                    : <Box display="flex" flexDirection="column" gap={2}>
                        <Box display="flex" flexDirection="column" gap={2}>
                            <TextField
                                required
                                fullWidth
                                label="Group Name"
                                variant="outlined"
                                margin="normal"
                                {...nameField}
                            />
                            <TextField
                                select
                                value={settings.groupCategory || ''}
                                label="Category"
                                helperText="Please select a category or clear the selection"
                                fullWidth
                                onChange={handleCategoryChange}
                                SelectProps={{
                                    renderValue: (selected) => {
                                        if (!selected) return 'No category selected';
                                        const selectedCategory = formCategories.find((category) => category.id === selected);
                                        if (!selectedCategory) return 'Invalid category selected';
                                        return (
                                            <Box display="flex" alignItems="center">
                                                <Box
                                                    sx={{
                                                        width: 12,
                                                        height: 12,
                                                        borderRadius: '50%',
                                                        backgroundColor: selectedCategory.color || 'grey',
                                                        marginRight: 1,
                                                    }}
                                                />
                                                {selectedCategory.name || ''}
                                            </Box>
                                        );
                                    },
                                }}
                            >
                                <MenuItem value="">
                                    <Box display="flex" alignItems="center" sx={{color: 'grey'}}>
                                        <Box
                                            sx={{
                                                width: 12,
                                                height: 12,
                                                borderRadius: '50%',
                                                backgroundColor: 'transparent',
                                                marginRight: 1,
                                            }}
                                        />
                                        No category
                                    </Box>
                                </MenuItem>
                                {formCategories.length > 0 &&
                                    formCategories.map(({id, name, color}) => (
                                        <MenuItem key={id} value={id}>
                                            <Box display="flex" alignItems="center">
                                                <Box
                                                    sx={{
                                                        width: 12,
                                                        height: 12,
                                                        borderRadius: '50%',
                                                        backgroundColor: color || 'grey',
                                                        marginRight: 1,
                                                    }}
                                                />
                                                {name}
                                            </Box>
                                        </MenuItem>
                                    ))}
                            </TextField>
                            <Stack flexDirection="column">
                                <FormControl>
                                    <FormGroup
                                        row
                                        sx={{
                                            alignItems: 'center',
                                            gap: 1,
                                        }}>
                                        <FormLabel
                                            id="members-form-control-label-placement"
                                        >Members:</FormLabel>
                                        <RadioGroup
                                            disabled={editMode || group.parentGroupFilterId}
                                            row
                                            aria-labelledby="members-form-control-label-placement"
                                            name="members"
                                            defaultValue={GROUP_MEMBERS_TYPE.students}
                                            value={settings.membersType}
                                            onChange={handleMembersTypeChange}
                                        >
                                            <FormControlLabel disabled={editMode || group.parentGroupFilterId} value={GROUP_MEMBERS_TYPE.students}
                                                              control={<Radio/>}
                                                              label="Students"/>
                                            <FormControlLabel disabled={editMode || group.parentGroupFilterId} value={GROUP_MEMBERS_TYPE.employee}
                                                              control={<Radio/>}
                                                              label="Employee"/>
                                        </RadioGroup>
                                    </FormGroup>
                                </FormControl>
                                <FormControlLabel
                                    disabled={editMode}
                                    control={
                                        <Checkbox
                                            checked={isGroupDynamic}
                                            onChange={(e) => setIsGroupDynamic(e.target.checked)}
                                        />
                                    }
                                    label={`Dynamic Group (auto-include ${memberTypeTitlePlural.toLowerCase()})`}
                                />
                            </Stack>
                        </Box>
                        <Divider/>
                        <FiltersTab
                            data={filteredGroupFilters}
                            settings={settings}
                            strictRecipientsFiltering={strictRecipientsFiltering}
                            disableStrictRecipientsFiltering={editMode}
                            setStrictRecipientsFiltering={setStrictRecipientsFiltering}
                            setSettings={setSettings}
                            disableEnquirySelect={
                                settings.membersType === GROUP_MEMBERS_TYPE.employee
                                    ? true
                                    : schoolInfo == null
                                        ? true
                                        : schoolInfo.disableEnquiriesSync
                            }
                            hideStudentSelect={settings.membersType === GROUP_MEMBERS_TYPE.employee}
                            hideApplicationSelect={settings.membersType === GROUP_MEMBERS_TYPE.employee}
                            memberType={settings.membersType}
                            selectionSubHeader={
                                settings.membersType === GROUP_MEMBERS_TYPE.employee
                                    ? 'Select employee filters and/or individual employees.'
                                    : 'Select student filters and/or individual students.'
                            }
                            groupFiltersLabel={
                                settings.membersType === GROUP_MEMBERS_TYPE.employee
                                    ? 'Employee Filters'
                                    : 'Student Filters'
                            }
                            groupFilterSelectLabel="Filters"
                        />
                        {isStudentDynamicGroup && (
                            <>
                                <Divider/>
                                <SettingsSectionBase header="Status Update Configuration">
                                    <Stack spacing={2}>
                                        <Typography variant="body2" color="text.secondary">
                                            Control automatic application status updates for students in this group.
                                        </Typography>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={settings.statusUpdateEnabled}
                                                    onChange={(e) =>
                                                        setSettings((prev) => ({
                                                            ...prev,
                                                            statusUpdateEnabled: e.target.checked,
                                                            membershipSetStatusId: e.target.checked ? prev.membershipSetStatusId : '',
                                                            membershipFromStatusId: e.target.checked ? prev.membershipFromStatusId : ''
                                                        }))
                                                    }
                                                />
                                            }
                                            label="Enable automatic status updates"
                                        />
                                        {settings.statusUpdateEnabled && (
                                            <Stack spacing={2}>
                                                <Alert severity="info">
                                                    From Status is optional: pick a status to narrow who is updated, or Any current status for
                                                    all eligible students.
                                                </Alert>
                                                <FormControl fullWidth required>
                                                    <InputLabel>To Status</InputLabel>
                                                    <Select
                                                        value={settings.membershipSetStatusId}
                                                        label="To Status"
                                                        onChange={(e) => setSettings((prev) => ({
                                                            ...prev,
                                                            membershipSetStatusId: e.target.value
                                                        }))}
                                                    >
                                                        {applicationStatuses.map((status) => (
                                                            <MenuItem key={status.id} value={status.id}>
                                                                {status.description}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                                <FormControl fullWidth>
                                                    <InputLabel>From Status (optional)</InputLabel>
                                                    <Select
                                                        value={settings.membershipFromStatusId}
                                                        label="From Status (optional)"
                                                        onChange={(e) => setSettings((prev) => ({
                                                            ...prev,
                                                            membershipFromStatusId: e.target.value
                                                        }))}
                                                    >
                                                        <MenuItem value="">
                                                            <em>Any current status</em>
                                                        </MenuItem>
                                                        {applicationStatuses.map((status) => (
                                                            <MenuItem key={status.id} value={status.id}>
                                                                {status.description}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Stack>
                                        )}
                                    </Stack>
                                </SettingsSectionBase>
                            </>
                        )}
                        {
                            group.linkedForms?.length > 0 && <Divider/>
                        }
                        <LinkedForms group={group}/>
                    </Box>
            }
        </AdmicityDialog>
    );
};

/* eslint-disable react/prop-types */
function LoadingSkeleton() {
    return (
        <Stack spacing={2}>
            <Skeleton variant="rectangular" height={56} sx={{borderRadius: 1}}/>
            <Skeleton variant="rectangular" height={56} sx={{borderRadius: 1}}/>
            <Skeleton variant="rectangular" height={56} sx={{borderRadius: 1}}/>
            <Box>
                <Stack direction="row" spacing={3} alignItems="center" mt={1}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Skeleton variant="circular" width={20} height={20}/>
                        <Skeleton variant="text" width={80}/>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Skeleton variant="circular" width={20} height={20}/>
                        <Skeleton variant="text" width={90}/>
                    </Stack>
                </Stack>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
                <Skeleton variant="rectangular" width={20} height={20} sx={{borderRadius: 1}}/>
                <Skeleton variant="text" width={230}/>
            </Stack>
            <Divider/>
            <Box>
                <Skeleton variant="text" width="40%"/>
                <Skeleton variant="text" width="60%"/>
                <Skeleton variant="rectangular" height={56} sx={{borderRadius: 1, mt: 1}}/>
                <Stack direction="row" spacing={1} justifyContent="flex-end" mt={1}>
                    <Skeleton variant="rectangular" width={100} height={36} sx={{borderRadius: 1}}/>
                    <Skeleton variant="rectangular" width={100} height={36} sx={{borderRadius: 1}}/>
                </Stack>
            </Box>

            <Box>
                <Skeleton variant="text" width="40%"/>
                <Skeleton variant="text" width="60%"/>
                <Stack direction="column" spacing={1} mt={1}>
                    <Skeleton variant="rectangular" height={56} sx={{borderRadius: 1}}/>
                    <Skeleton variant="rectangular" height={56} sx={{borderRadius: 1}}/>
                </Stack>
            </Box>
        </Stack>
    );
}

function LinkedForms({group}) {
    if (!group.linkedForms || group.linkedForms.length === 0) {
        return null;
    }

    return (
        <SettingsSectionBase
            header="Linked Forms"
            headerIcon={<LinkIcon fontSize="small"/>}
        >
            <List disablePadding>
                {group.linkedForms?.map((x, i) => (
                    <ListItem
                        key={i}
                        alignItems="flex-start"
                        disableGutters
                        sx={{
                            flexDirection: 'column',
                            alignItems: 'stretch',
                            border: '1px solid #e0e0e0',
                            borderRadius: 1,
                            paddingLeft: 2,
                            paddingRight: 2,
                            marginBottom: 2
                        }}
                    >
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <Typography color="text.secondary" sx={{display: 'flex', alignItems: 'center'}}>
                                <TextSnippetOutlinedIcon sx={{fontSize: 16, marginRight: 1}}/>
                                {x.name}
                            </Typography>
                            {/*<IconButton edge="end" size="small" onClick={() => onRemove(i)}>*/}
                            {/*    <DeleteIcon fontSize="small"/>*/}
                            {/*</IconButton>*/}
                        </Box>
                        <Typography variant="body2">
                            {x.question} — <strong>{x.questionType === 'checkboxQuestion'
                            ? getCheckboxDisplayLabel(x.answer)
                            : x.answer}</strong>
                        </Typography>
                    </ListItem>
                ))}
            </List>
        </SettingsSectionBase>
    );
}

/* eslint-enable react/prop-types */

CreateOrUpdateGroupDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    title: PropTypes.string.isRequired,
    group: PropTypes.object.isRequired,
    formCategories: PropTypes.array.isRequired,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    groupFilters: PropTypes.array.isRequired,
    editMode: PropTypes.bool,
    isLoading: PropTypes.bool
};

export default CreateOrUpdateGroupDialog;
